/**
 * AppInstallAndPushPrompt — single bottom-of-screen banner that
 * handles BOTH the PWA install nudge AND the push permission ask.
 *
 * Why combined: showing two separate banners back-to-back is
 * annoying. On non-installed browsers we lead with "Install the app"
 * because installation unlocks iOS push AND a better daily-use UX.
 * Once installed (or on platforms where install isn't applicable),
 * we pivot to "Turn on notifications".
 *
 * Visibility logic:
 *   • Hidden if user dismissed (localStorage `install_push_dismissed_v1`)
 *   • Hidden if not authed
 *   • Hidden if user is already subscribed to push
 *   • Hidden on landing / signup / login routes
 *   • Hidden until user has been on the site for ≥ 20 seconds —
 *     don't ambush brand new visitors.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BellIcon as Bell, XIcon as X, DeviceMobileIcon as DeviceMobile, AppleLogoIcon as AppleLogo, BellRingingIcon as BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  pushIsSupported, getPushState, subscribeUserToPush, registerServiceWorker,
} from "@/lib/push";

const STORAGE_KEY = "install_push_dismissed_v1";
const SHOW_AFTER_MS = 20_000;
const HIDDEN_ROUTES = new Set([
  "/", "/login", "/signup", "/forgot-password", "/reset-password",
]);

export default function AppInstallAndPushPrompt({ user }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [pushState, setPushState] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  // Native app: never show install/PWA-push prompts — the user is
  // already inside the installed app (native push lives in Settings).
  const inNative = typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();

  // Detect "already installed" (standalone display mode). Most install
  // affordances disappear in this state — perfect time to pivot to push.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(display-mode: standalone)");
    const update = () => setIsStandalone(mq.matches || window.navigator.standalone === true);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Capture beforeinstallprompt so we can trigger it on user gesture.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Register service worker + read current push state once on mount.
  useEffect(() => {
    if (!user) return;
    registerServiceWorker();
    getPushState().then(setPushState);
  }, [user]);

  // Reveal the banner after a delay so brand-new visitors aren't ambushed.
  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return; // dismissed
    } catch { /* private mode — show anyway */ }
    const t = setTimeout(() => setOpen(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [user]);

  if (inNative) return null;
  if (!user) return null;
  if (!open) return null;
  if (HIDDEN_ROUTES.has(location.pathname)) return null;
  if (pushState?.subscribed) return null;
  // Nothing to offer: not installable AND push not supported.
  if (!installEvent && !isStandalone && !pushIsSupported()) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  const remindLater = () => {
    // Don't write to localStorage — the banner will re-appear on next visit.
    setOpen(false);
  };

  const enablePush = async () => {
    if (!pushIsSupported()) {
      toast.error("Your browser doesn't support push notifications.");
      return;
    }
    const r = await subscribeUserToPush();
    if (r.ok) {
      toast.success("Notifications turned on. We'll send a test now.");
      try { await import("@/lib/push").then((m) => m.sendTestPush()); } catch { /* ignore */ }
      dismiss();
    } else if (r.reason === "denied") {
      toast.error("Permission denied. You can enable later from your browser settings.");
      dismiss();
    } else {
      toast.error("Couldn't turn on push — try again.");
    }
  };

  const installApp = async () => {
    if (!installEvent) return;
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice.outcome === "accepted") {
        toast.success("Armstrong K9 installed. Look for it on your home screen.");
      }
    } catch { /* user dismissed */ }
    setInstallEvent(null);
    // After install, encourage push next time the banner returns.
    setOpen(false);
  };

  // iOS detection — A2HS requires manual guidance (no programmatic prompt).
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

  // Two states: installable / already installed.
  // Compose the right CTA per platform.
  let mode;
  if (installEvent) mode = "install-prompt";
  else if (isIos && !isStandalone) mode = "ios-a2hs";
  else mode = "push";

  return (
    <div
      data-testid="app-install-push-prompt"
      data-mode={mode}
      className="fixed left-1/2 -translate-x-1/2 z-[60] w-[min(96vw,560px)] bottom-24 md:bottom-6"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="rounded-2xl border border-[#E5E5E5] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.16)] backdrop-blur-md p-4 sm:p-5">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss forever"
          data-testid="app-install-push-dismiss"
          className="absolute top-2 right-2 inline-flex items-center justify-center h-7 w-7 rounded-full text-[#6B6B6B] hover:text-ink hover:bg-[#FAFAFA] transition-colors"
        >
          <X size={14} weight="bold" />
        </button>
        <div className="flex items-start gap-3 pr-7">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-ink text-gold flex-shrink-0">
            {mode === "install-prompt" || mode === "ios-a2hs" ? (
              <DeviceMobile size={18} weight="duotone" />
            ) : (
              <BellRinging size={18} weight="duotone" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-1">
              {mode === "install-prompt" || mode === "ios-a2hs" ? "Install the app" : "Stay in the loop"}
            </p>
            <h3 className="font-spectral text-lg sm:text-xl tracking-tight leading-tight mb-1">
              {mode === "install-prompt" && "Install Armstrong K9"}
              {mode === "ios-a2hs" && "Add to your Home Screen"}
              {mode === "push" && "Turn on notifications"}
            </h3>
            <p className="text-[12px] text-[#3F3F3F] leading-relaxed">
              {mode === "install-prompt" && "One tap to launch from your home screen. Push notifications for bids, inquiries, and transfers — even when the app is closed."}
              {mode === "ios-a2hs" && "Tap the Share button → 'Add to Home Screen'. Required on iOS for notifications to reach your phone's notification shade."}
              {mode === "push" && "Get pinged on your phone or desktop the moment something happens — bids, inquiries, transfers, payouts, and more."}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {mode === "install-prompt" && (
                <Button
                  onClick={installApp}
                  size="sm"
                  data-testid="app-install-cta"
                  className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4 text-[12px]"
                >
                  Install app
                </Button>
              )}
              {mode === "ios-a2hs" && (
                <Button
                  asChild
                  size="sm"
                  data-testid="ios-a2hs-cta"
                  className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4 text-[12px]"
                >
                  <Link to="/install" onClick={() => setOpen(false)}>
                    <AppleLogo size={14} weight="fill" className="mr-1" /> Show me how
                  </Link>
                </Button>
              )}
              {mode === "push" && (
                <Button
                  onClick={enablePush}
                  size="sm"
                  data-testid="app-push-cta"
                  className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4 text-[12px]"
                >
                  <Bell size={14} weight="fill" className="mr-1" /> Enable notifications
                </Button>
              )}
              <Button
                onClick={remindLater}
                size="sm"
                variant="ghost"
                className="rounded-full h-9 px-3 text-[12px] text-[#6B6B6B] hover:text-ink"
                data-testid="app-install-push-later"
              >
                Maybe later
              </Button>
            </div>
            {mode === "ios-a2hs" && showIosHelp && (
              <ol className="mt-3 pl-4 list-decimal text-[12px] text-[#3F3F3F] space-y-1">
                <li>Tap the <b>Share</b> button at the bottom of Safari (the square with an arrow up).</li>
                <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
                <li>Tap <b>Add</b> in the top-right.</li>
                <li>Open Armstrong K9 from your home screen and come back here to turn on notifications.</li>
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
